/**
 * Assistant — the full-page Ask BLIP view: your past conversations on the
 * left, the open one on the right.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AssistantConversation } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';
import { AssistantThread } from '../components/AssistantThread';
import { startersFor } from '../components/AskBlipPanel';

export function Assistant(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const current = params.get('c');
  const [items, setItems] = useState<AssistantConversation[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.assistant
      .list()
      .then((r) => setItems(r.items))
      .catch((e: unknown) =>
        setError(
          e instanceof ApiClientError
            ? e.message
            : 'Could not load conversations',
        ),
      );
  }, []);
  useEffect(load, [load]);

  const open = (id: string | null) => setParams(id ? { c: id } : {});

  return (
    <div className="ab-page">
      <div className="page-head">
        <div>
          <h1>Ask BLIP</h1>
          <p className="muted">
            A read-only assistant for questions about BLIP — how it works and
            what's in it. It can't change anything; change requests go to Ramos.
          </p>
        </div>
      </div>
      {error && <div className="form-error">{error}</div>}
      <div className="ab-page-grid">
        <nav className="ab-history card">
          <button
            type="button"
            className="btn btn-primary ab-new"
            onClick={() => open(null)}
          >
            + New conversation
          </button>
          {items.length === 0 && (
            <p className="muted small">No conversations yet.</p>
          )}
          <ul>
            {items.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className={
                    c.id === current ? 'ab-hist-item active' : 'ab-hist-item'
                  }
                  onClick={() => open(c.id)}
                >
                  <span className="ab-hist-title">{c.title}</span>
                  <span className="muted small">
                    {new Date(c.updatedAt).toLocaleString()}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <section className="ab-page-thread card">
          <AssistantThread
            key={current ?? 'new'}
            conversationId={current}
            onConversation={(id) => {
              open(id);
              load();
            }}
            page={{ route: '/assistant', kind: 'other' }}
            starters={startersFor({ route: '/assistant', kind: 'other' })}
          />
        </section>
      </div>
    </div>
  );
}
